import React, { useState, useEffect } from 'react';
import { empresasAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Building2, Edit, Trash2, RefreshCw } from 'lucide-react';

const Empresas = () => {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: '',
    ativo: true
  });

  useEffect(() => {
    loadEmpresas();
  }, []);

  const loadEmpresas = async () => {
    setLoading(true);
    try {
      const response = await empresasAPI.list();
      setEmpresas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editando) {
        await empresasAPI.update(editando.id, formData);
        toast.success('Empresa atualizada com sucesso!');
      } else {
        await empresasAPI.create(formData);
        toast.success('Empresa criada com sucesso!');
      }
      
      setDialogOpen(false);
      setEditando(null);
      setFormData({ nome: '', ativo: true });
      loadEmpresas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (empresa) => {
    setEditando(empresa);
    setFormData({
      nome: empresa.nome,
      ativo: empresa.ativo
    });
    setDialogOpen(true);
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta empresa?')) return;

    try {
      await empresasAPI.delete(id);
      toast.success('Empresa deletada com sucesso!');
      loadEmpresas();
    } catch (error) {
      toast.error('Erro ao deletar empresa');
    }
  };

  const handleNovo = () => {
    setEditando(null);
    setFormData({ nome: '', ativo: true });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciar Empresas</h1>
          <p className="text-gray-500 mt-1">Administre as empresas do sistema</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadEmpresas} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Atualizar
          </Button>
          <Button onClick={handleNovo}>
            <Building2 className="mr-2 h-4 w-4" />
            Nova Empresa
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Empresas Cadastradas: {empresas.length}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {empresas.map((empresa) => (
              <div
                key={empresa.id}
                className="p-4 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{empresa.nome}</h3>
                    {!empresa.ativo && (
                      <Badge className="bg-gray-100 text-gray-800 mt-2">Inativo</Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditar(empresa)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeletar(empresa.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nome da Empresa *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ativo"
                checked={formData.ativo}
                onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
              />
              <Label htmlFor="ativo">Ativo</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Empresas;